import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { Camera, Upload, Trash2, Loader2, AlertTriangle, User } from 'lucide-react';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



export function ProfilePhotoUpload({ currentAvatarUrl, artistName, onUploadSuccess, getAuthHeaders }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, or WEBP.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!preview) return;

    setUploading(true);
    try {
      // Convert base64 to blob
      const response = await fetch(preview);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append('file', blob, 'profile.jpg');

      const uploadResponse = await fetch(`${API}/upload/profile-photo`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (uploadResponse.ok) {
        const data = await uploadResponse.json();
        toast.success('Profile photo uploaded successfully!');
        setPreview(null);
        if (onUploadSuccess) onUploadSuccess(data.avatar_url);
      } else {
        const error = await uploadResponse.json();
        
        // Handle moderation rejection
        if (error.detail?.violations) {
          toast.error(
            <div>
              <p className="font-bold">Image flagged for review</p>
              <p className="text-sm">{error.detail.reason}</p>
              <p className="text-xs text-yellow-400 mt-1">
                {error.detail.warning}
              </p>
            </div>
          );
        } else {
          toast.error(error.detail?.message || error.detail || 'Upload failed');
        }
      }
    } catch (error) {
      toast.error('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API}/upload/profile-photo`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });

      if (response.ok) {
        toast.success('Profile photo removed');
        if (onUploadSuccess) onUploadSuccess(null);
      } else {
        toast.error('Failed to remove photo');
      }
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const cancelPreview = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayUrl = preview || (currentAvatarUrl ? `${API.replace('/api', '')}${currentAvatarUrl}` : null);

  return (
    <Card className="glass-card border-border p-6" data-testid="profile-photo-upload">
      <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-4 flex items-center gap-2">
        <Camera className="w-4 h-4" />
        Profile Photo
      </h3>

      <div className="flex flex-col sm:flex-row gap-6 items-start">
        {/* Photo Preview */}
        <div className="flex-shrink-0">
          <div 
            className={`w-32 h-32 rounded-xl overflow-hidden border-2 transition-all ${
              dragOver ? 'border-secondary scale-105' : 'border-border'
            } ${!displayUrl ? 'bg-muted/30' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {displayUrl ? (
              <img 
                src={displayUrl} 
                alt={artistName || 'Profile'} 
                className="w-full h-full object-cover"
                data-testid="profile-photo-preview"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-12 h-12 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Upload Controls */}
        <div className="flex-1 space-y-4">
          {!preview ? (
            <>
              {/* Upload Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                  dragOver ? 'border-secondary bg-secondary/10' : 'border-border hover:border-border'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Drag & drop or click to upload
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG, WEBP • Max 5MB • Min 400×400
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleInputChange}
                className="hidden"
                data-testid="photo-file-input"
              />

              {/* Delete Button */}
              {currentAvatarUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                  data-testid="delete-photo-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Photo
                </Button>
              )}
            </>
          ) : (
            <>
              {/* Preview Actions */}
              <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/10 border border-secondary/30">
                <AlertTriangle className="w-4 h-4 text-secondary" />
                <span className="text-sm">Preview - Click Save to upload</span>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="bg-secondary hover:bg-secondary/90"
                  data-testid="save-photo-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Save Photo
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={cancelPreview}
                  disabled={uploading}
                  data-testid="cancel-photo-btn"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}

          {/* Guidelines */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p>• Square photos work best (auto-cropped if not)</p>
            <p>• No nudity, hate symbols, or explicit content</p>
            <p>• Photos are reviewed by AI moderation</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default ProfilePhotoUpload;
